import { action, makeObservable, observable } from 'mobx'
import { inject, injectable } from 'inversify'
import { RouterGateway } from './router.gateway'
import { Types } from '../ioc/types'
import { RoutesConfig, type RouteConfigEntry, type RouteId } from './router.config'
import type { LoggingGateway } from '../services/logging.gateway'
import type { LocalStorageGateway } from '../services/local-storage.gateway'
import type { UserScopedState } from '../interfaces/user-scoped-state'
import { z } from '@core/validation/validation.schema'

type CurrentRoute = Readonly<{
  routeId: RouteId | null
  routeDef: null | Readonly<RouteConfigEntry['routeDef']>
  params: Readonly<Record<string, string>>
  query: Readonly<Record<string, string>>
}>

export type MatchedUrl = {
  routeId: RouteId
  params: Record<string, string>
  query: Record<string, string>
}

export type LastRoute = {
  routeId: RouteId
  params: Record<string, string>
  query: Record<string, string>
}

const LAST_ROUTE_STORAGE_KEY = 'last-route'
const LastRouteSchema = z.object({
  routeId: z.string(),
  params: z.record(z.string(), z.string()).default({}),
  query: z.record(z.string(), z.string()).default({}),
})

/**
 * Source of truth for the current route and its persistence across a reload
 * — the last-route restore that made the user-scoped-state discipline
 * necessary in the first place (see `UserScopedState`'s own class comment).
 */
@injectable()
export class RouterRepository implements UserScopedState {
  currentRoute: CurrentRoute = {
    routeId: null,
    routeDef: null,
    params: {},
    query: {},
  }

  @inject(RoutesConfig)
  routesConfig!: RoutesConfig

  @inject(Types.IRouterGateway)
  routerGateway!: RouterGateway

  @inject(Types.ILoggingGateway)
  private loggingGateway!: LoggingGateway

  @inject(Types.ILocalStorageGateway)
  private localStorageGateway!: LocalStorageGateway

  onRouteChanged: () => void = null!

  constructor() {
    makeObservable(this, {
      currentRoute: observable,
      setCurrentRoute: action,
    })
  }

  /**
   * The only way currentRoute changes. Router used to assign the four fields
   * one by one from outside, which meant observers were notified four times per
   * navigation and could briefly read a new routeId next to the previous
   * route's params. One action writes them together, in one transaction.
   */
  setCurrentRoute = (
    routeId: RouteId,
    routeDef: RouteConfigEntry['routeDef'],
    params: Record<string, string>,
    query: Record<string, string>,
  ) => {
    // A whole replacement is the atomic publication; the copies ensure the
    // caller cannot mutate the committed route through a shared reference.
    this.currentRoute = {
      routeId,
      routeDef: { ...routeDef },
      params: { ...params },
      query: { ...query },
    }
  }

  /**
   * The resume point for a bare '/' launch. Deliberately *not* observable —
   * nothing renders it, it is read once per page load by Router's RESUME check.
   * Making it observable would only add notifications nobody listens to.
   */
  readLastRoute(): LastRoute | null {
    const stored = this.localStorageGateway.getValue(LAST_ROUTE_STORAGE_KEY)
    const parsed = LastRouteSchema.safeParse(stored)

    // Storage is outside input, and a route id that no longer exists in the
    // config would resume into nothing.
    if (!parsed.success || !this.findRoute(parsed.data.routeId)) return null

    return {
      routeId: parsed.data.routeId as RouteId,
      params: { ...parsed.data.params },
      query: { ...parsed.data.query },
    }
  }

  writeLastRoute(lastRoute: LastRoute): void {
    this.localStorageGateway.setValue(LAST_ROUTE_STORAGE_KEY, lastRoute)
  }

  /**
   * Where the *previous* user was is their business, not the next one's. Without
   * this, A logging out and B logging in meant B's bare `/` launch resumed into
   * A's last page — the same cross-user leak the Books/Assets reset already
   * covered, missed because the resume point is persisted rather than in memory.
   */
  resetUserScopedState(): void {
    this.localStorageGateway.remove(LAST_ROUTE_STORAGE_KEY)
  }

  registerRoutes(
    updateCurrentRoute: (
      routeId: RouteId,
      params: Record<string, string>,
      query: Record<string, string>,
    ) => Promise<boolean>,
    onRouteChanged: () => void,
  ) {
    this.onRouteChanged = onRouteChanged

    this.routerGateway.listen((url) => {
      const match = this.matchUrl(url)
      return updateCurrentRoute(match.routeId, match.params, match.query)
    })
  }

  /** Resolves `true` only once the transition this URL triggers has committed — see router.gateway.ts. */
  async goToId(
    id: RouteId,
    params: Record<string, string> = {},
    queryParams: Record<string, string> = {},
  ): Promise<boolean> {
    const route = this.findRoute(id)
    if (!route) {
      this.loggingGateway.warn(`goToId: no route registered for id "${id}" — ignored`)
      return false
    }

    return this.routerGateway.navigate(this.buildUrl(route, params, queryParams))
  }

  /**
   * Puts a new query string on the page the user is already on, without
   * navigating. The URL codec stays here (invariant 12: the gateway builds no
   * URLs); what the gateway does with it is deliberately not a navigation — see
   * RouterGateway.replaceUrl.
   *
   * `currentRoute` is left alone on purpose: nothing about the route changed,
   * and rewriting it here would make every list's own filter state look like a
   * route transition to everything watching one.
   */
  replaceQuery(query: Record<string, string>): void {
    const routeId = this.currentRoute.routeId
    if (routeId === null) return

    const route = this.findRoute(routeId)
    if (!route) return

    this.routerGateway.replaceUrl(this.buildUrl(route, this.currentRoute.params, query))
  }

  findRoute(routeId: string): RouteConfigEntry | undefined {
    return this.routesConfig.routes.find((r) => r.routeId === routeId)
  }

  /**
   * URL codec, encode direction: route + params + query → URL string.
   * Pure — no gateway, no Navigo.
   */
  buildUrl(route: RouteConfigEntry, params: Record<string, string> = {}, query: Record<string, string> = {}): string {
    let path = route.routeDef.path
    for (const key in params) {
      path = path.replace(`:${key}`, encodeURIComponent(params[key]))
    }
    if (!path.startsWith('/')) path = `/${path}`

    const queryString = new URLSearchParams(query).toString()
    return queryString ? `${path}?${queryString}` : path
  }

  /**
   * URL codec, decode direction: URL string → route id + params + query.
   * Pure — segments matched against the route table; no match → wildcard route.
   */
  matchUrl(url: string): MatchedUrl {
    const [pathPart, queryString = ''] = url.split('?')
    const query = Object.fromEntries(new URLSearchParams(queryString).entries())
    const urlSegments = pathPart.replace(/^\//, '').split('/')

    for (const route of this.routesConfig.routes) {
      if (route.routeDef.path === '*') continue

      const patternSegments = route.routeDef.path.replace(/^\//, '').split('/')
      if (patternSegments.length !== urlSegments.length) continue

      const params: Record<string, string> = {}
      const matched = patternSegments.every((pattern, index) => {
        if (pattern.startsWith(':')) {
          params[pattern.slice(1)] = decodeURIComponent(urlSegments[index])
          return true
        }
        return pattern === urlSegments[index]
      })

      if (matched) return { routeId: route.routeId, params, query }
    }

    const wildcard = this.routesConfig.routes.find((r) => r.routeDef.path === '*')!
    return { routeId: wildcard.routeId, params: {}, query }
  }
}
